import { 
  Badge, 
  Card, 
  Container, 
  Group, 
  Pagination, 
  Paper, 
  Stack, 
  Text, 
  ThemeIcon,
  Title 
} from '@mantine/core';
import Loader from '../../components/Loader';
import { 
  IconCalendar, 
  IconCheck,
  IconClipboardList, 
  IconClock, 
  IconCurrencyNaira,
  IconFileText,
  IconX 
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Endpoints } from '../../lib/endpoints';
import { usePerformRequest } from '../../lib/usePerformRequest';
import { IPaginationProps, PaginatedResponse } from '../../types';
import { ILoanRequestProps } from '../../types/Loan';

export default function Dashboard() {
  const [pagination, setPagination] = useState<IPaginationProps>({
    page: 1,
    limit: 10,
  });
  
  const loanHistoryEndpoint = Endpoints.GetLoanHistory(pagination.page, pagination.limit);
  const {data: loanRequests, isLoading, reloadData: reloadLoanRequests} = usePerformRequest<PaginatedResponse<ILoanRequestProps[]>>({
    method: loanHistoryEndpoint.method,
    route: loanHistoryEndpoint.route,
  })

  useEffect(() => {
    reloadLoanRequests();
  }, [pagination.page, pagination.limit, reloadLoanRequests]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      default:
        return 'yellow';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return IconCheck;
      case 'rejected':
        return IconX;
      default:
        return IconClock;
    }
  };

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString('en-NG')}`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const totalPages = loanRequests ? Math.ceil(loanRequests.totalRecords / loanRequests.limit) : 1;

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Group justify="space-between" align="flex-end" mb="md">
            <div>
              <Title 
                order={1} 
                size="3rem" 
                fw={800}
                style={{ color: '#679369', marginBottom: '0.5rem' }}
              >
                Loan History
              </Title>
              <Text size="lg" c="dimmed">
                Track and manage all your loan applications
              </Text>
            </div>
            
            {loanRequests?.data && loanRequests.data.length > 0 && (
              <Paper
                p="md"
                radius="sm"
                style={{
                  background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
                  border: '1px solid #699269',
                }}
              >
                <Group gap="xs">
                  <ThemeIcon size="lg" radius="sm" style={{ background: '#699269' }}>
                    <IconFileText size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" fw={500} c="#558B2F">
                      Total Applications
                    </Text>
                    <Text size="xl" fw={700} c="#699269">
                      {loanRequests.totalRecords || 0}
                    </Text>
                  </div>
                </Group>
              </Paper>
            )}
          </Group>
        </motion.div>

        {/* Loading State */}
        {isLoading ? (
          <Paper
            shadow="sm"
            p="xl"
            radius="sm"
            style={{
              background: 'white',
              border: '2px solid #e5e7eb',
            }}
          >
            <Stack align="center" gap="lg" py="xl">
              <Loader size="xl" />
              <Text size="lg" c="dimmed">
                Loading your loan history...
              </Text>
            </Stack>
          </Paper>
        ) : !loanRequests?.data || loanRequests.data.length === 0 ? (
          /* Empty State */
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Paper
              shadow="md"
              p="xl"
              radius="sm"
              style={{
                background: 'linear-gradient(135deg, #f9fafb 0%, #E8F5E9 100%)',
                border: '2px solid #e5e7eb',
              }}
            >
              <Stack align="center" gap="xl" py="xl">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                >
                  <ThemeIcon
                    size={120}
                    radius="md"
                    style={{
                      background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
                    }}
                  >
                    <IconClipboardList size={60} style={{ color: '#699269' }} />
                  </ThemeIcon>
                </motion.div>
                <div style={{ textAlign: 'center' }}>
                  <Title order={2} fw={700} mb="sm" style={{ color: '#111827' }}>
                    No loan applications yet
                  </Title>
                  <Text size="lg" c="dimmed" style={{ maxWidth: '500px' }}>
                    You haven't submitted any loan requests yet. Visit the Request Loan page to apply for your first loan.
                  </Text>
                </div>
              </Stack>
            </Paper>
          </motion.div>
        ) : (
          /* Loan History Cards */
          <Stack gap="lg">
            {loanRequests.data.map((loan, index) => {
              const StatusIcon = getStatusIcon(loan.status);
              return (
                <motion.div
                  key={loan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Card
                    shadow="md"
                    padding="xl"
                    radius="sm"
                    style={{
                      background: 'white',
                      border: '2px solid #e5e7eb',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    className="hover:shadow-2xl"
                  >
                    {/* Status Indicator Bar */}
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background:
                          loan.status === 'approved'
                            ? 'linear-gradient(90deg, #699269 0%, #81C784 100%)'
                            : loan.status === 'rejected'
                            ? 'linear-gradient(90deg, #dc2626 0%, #ef4444 100%)'
                            : 'linear-gradient(90deg, #eab308 0%, #facc15 100%)',
                      }}
                    />

                    <Stack gap="lg">
                      <Group justify="space-between" align="flex-start">
                        <Group gap="md" align="flex-start">
                          <ThemeIcon
                            size={56}
                            radius="sm"
                            style={{
                              background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
                            }}
                          >
                            <IconCurrencyNaira size={32} style={{ color: '#699269' }} />
                          </ThemeIcon>
                          <div>
                            <Text size="xs" c="dimmed" fw={500} tt="uppercase" mb={4}>
                              Loan Amount
                            </Text>
                            <Text size="2rem" fw={800} style={{ color: '#699269', lineHeight: 1 }}>
                              {formatCurrency(loan.amount)}
                            </Text>
                          </div>
                        </Group>

                        <Badge
                          size="lg"
                          radius="sm"
                          w={"fit-content"}
                          h={40}
                          color={getStatusColor(loan.status)}
                          
                        >
                          <div className="flex-row align-center">

                          <StatusIcon size={16} style={{ flexShrink: 0 }} />
                          <Text fz="sm" ml={5} fw={500}>
                          {loan.status}
                          </Text>
                          </div>
                        </Badge>
                      </Group>

                      {/* Purpose */}
                      <Paper
                        p="md"
                        radius="sm"
                        style={{
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                        }}
                      >
                        <Group gap="xs" mb="xs">
                          <IconFileText size={18} style={{ color: '#699269' }} />
                          <Text size="sm" fw={600} c="dimmed">
                            Purpose
                          </Text>
                        </Group>
                        <Text size="md" style={{ color: '#374151' }}>
                          {loan.purpose}
                        </Text>
                      </Paper>

                      {/* Details Grid */}
                      <Group grow>
                        <Paper
                          p="md"
                          radius="sm"
                          style={{
                            background: 'linear-gradient(135deg, #E8F5E9 0%, #ffffff 100%)',
                            border: '1px solid #C8E6C9',
                          }}
                        >
                          <Group gap="xs" mb="xs">
                            <IconCalendar size={18} style={{ color: '#699269' }} />
                            <Text size="xs" fw={600} c="#558B2F">
                              Duration
                            </Text>
                          </Group>
                          <Text size="lg" fw={700} c="#699269">
                            {loan.duration} months
                          </Text>
                        </Paper>

                        <Paper
                          p="md"
                          radius="sm"
                          style={{
                            background: 'linear-gradient(135deg, #E8F5E9 0%, #ffffff 100%)',
                            border: '1px solid #C8E6C9',
                          }}
                        >
                          <Group gap="xs" mb="xs">
                            <IconClock size={18} style={{ color: '#699269' }} />
                            <Text size="xs" fw={600} c="#558B2F">
                              Applied On
                            </Text>
                          </Group>
                          <Text size="sm" fw={600} c="#699269">
                            {formatDate(loan.createdAt)}
                          </Text>
                        </Paper>
                      </Group>
                    </Stack>
                  </Card>
                </motion.div>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <Paper
                  p="lg"
                  radius="sm"
                  style={{
                    background: 'white',
                    border: '2px solid #e5e7eb',
                  }}
                >
                  <Group justify="center">
                    <Pagination
                      total={totalPages}
                      value={pagination.page}
                      onChange={(page) => setPagination({ ...pagination, page })}
                      size="lg"
                      radius="sm"
                      color="green"
                      styles={{
                        control: {
                          border: '2px solid #e5e7eb',
                          '&[data-active]': {
                            background: 'linear-gradient(135deg, #699269 0%, #81C784 100%)',
                            border: '2px solid #699269',
                            fontWeight: 700,
                          },
                          '&:hover': {
                            background: '#E8F5E9',
                          },
                        },
                      }}
                    />
                  </Group>
                  <Text size="sm" c="dimmed" ta="center" mt="md">
                    Showing page {pagination.page} of {totalPages} • {loanRequests.totalRecords || 0} total applications
                  </Text>
                </Paper>
              </motion.div>
            )}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}

